import React, { useEffect, useState } from 'react';
import { List, Message, Title } from '@meitu/saas-ui';
import type { Editor } from '@meitu/saas-editor-core';
import { type WatermarkLayer } from '@meitu/saas-editor-core/es/layers/watermark-layer';
import { imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import { MaterialCard } from '@/components/material-card';
import { LOCAL_WATERMARK_CONFIRM, useWatermarkConfirm } from '@/components/watermark-confirm';
import { useLocaleStore } from '@/i18n';
import { ICategory, MaterialTopicItem, WatermarkListItem } from '@/types';
import { formatImageUrl } from '@/utils/file-kit';
import { AddWatermark, IAddWatermarkProps } from '../all-watermark-list';

const { BackTitle } = Title;

interface IWatermarkDetailList {
    editor: Editor;
    CODE: any;
    recordId?: number;
    showSvipCorner: boolean;
    setShowDetailsCollapse: Function;
    categoryInfo?: ICategory;
    isRecently?: boolean;
    style?: React.CSSProperties;
    isTagFilter?: boolean;
    fetchMaterialsByCategory: (params: any) => Promise<any>;
    fetchNewMaterialList: (params: any) => Promise<any>;
    fetchRecentUsedList: (params: any) => Promise<any>;
    fetchImageMaterialTopiDetail: (params: any) => Promise<any>;
    fetchSaveRecentUsed: (params: any) => Promise<any>;
    // 最近使用
    setRecentWatermarkUsedList: (data: MaterialTopicItem) => void;
    recentWatermarkUsedList?: MaterialTopicItem;
    // @FIXME 类型改成 IUserInfoParams
    userInfo: any;
    useListTrack: Function;
}
// 每个分页请求多少个素材
const Material_SIZE = 50;
const funName = 'sy';

const WatermarkDetailList = (props: IWatermarkDetailList) => {
    const { $t } = useLocaleStore();
    const {
        editor,
        userInfo,
        setShowDetailsCollapse,
        categoryInfo = {},
        CODE,
        showSvipCorner,
        isRecently = false,
        style,
        isTagFilter = false,
        recentWatermarkUsedList,
        setRecentWatermarkUsedList,
        recordId,
        useListTrack,
    } = props;
    const { id, name } = categoryInfo;
    const [materialList, setMaterialList] = useState<WatermarkListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [addData, setAddData] = useState<IAddWatermarkProps | null>(null);
    const [currentCursor, setCurrentCursor] = useState<string | null>(null);
    const [usedCursor, setUsedCursor] = useState<string | null>(null);

    const { watermarkConfirm } = useWatermarkConfirm();

    const funcID = '18_18';

    const fetchMaterialList = async (init = false) => {
        try {
            setLoading(true);

            if (!init && currentCursor === '') {
                setLoading(false);
                return;
            }

            let data, error_code;
            // 请求最新素材数据
            if (isRecently) {
                if (usedCursor === '') {
                    setLoading(false);
                    return;
                }
                // 请求常用素材
                const { data: resData } = await props.fetchRecentUsedList({
                    count: Material_SIZE,
                    code: 'watermark',
                    cursor: usedCursor || undefined,
                    module: 1,
                });

                if (resData.code === CODE.OK) {
                    data = {
                        ...resData.data,
                        usedCursor: resData.data?.cursor,
                        cursor: null,
                    };
                    error_code = resData.code;
                } else {
                    data = {
                        materials: [],
                        usedCursor: resData.data?.cursor,
                        cursor: null,
                    };
                    setHasMore(false);
                    setLoading(false);
                }

                setRecentWatermarkUsedList({
                    id: 'recentUsed',
                    materials: materialList.concat((data?.materials as WatermarkListItem[]) || []),
                    name: $t((v) => v.collapse.materialCollapse.commonUsed),
                    cursor: '',
                });
            } else if (id === 'newMaterial') {
                const { data: resData } = await props.fetchNewMaterialList({
                    count: Material_SIZE,
                    cursor: currentCursor || undefined,
                });

                data = resData.data;
                error_code = resData.code;
            } else if (isTagFilter) {
                if (!id) return;
                const { data: resData } = await props.fetchMaterialsByCategory({
                    cursor: currentCursor ?? '',
                    count: Material_SIZE,
                    id: id as number,
                });
                data = resData.data;
                error_code = resData.code;
            } else {
                if (!id) {
                    setLoading(false);
                    return;
                }
                // 默认请求专题素材
                const { data: resData } = await props.fetchImageMaterialTopiDetail({
                    count: Material_SIZE,
                    id: id as number,
                    cursor: currentCursor || undefined,
                });
                data = resData.data;
                error_code = resData.code;
            }

            if (error_code === CODE.OK && data && (data as any).materials) {
                const { cursor, usedCursor = null, materials } = data as any;
                materials &&
                    setMaterialList(
                        init
                            ? (materials as WatermarkListItem[])
                            : materialList.concat(materials as WatermarkListItem[]),
                    );
                if (usedCursor !== null) {
                    setUsedCursor(usedCursor || '');
                    setHasMore(!!usedCursor);
                } else {
                    setCurrentCursor(cursor || '');
                    setHasMore(!!cursor);
                }
                setLoading(false);
            }
        } catch (e) {
            console.log('获取列表失败', e);
        }
    };
    // useEffect(() => {
    //     if (isRecently && !userInfo) {
    //         setRecentUsedList({
    //             id: 'recentUsed',
    //             materials: [],
    //             name: '常用素材',
    //             cursor: ''
    //         });
    //     }
    // }, [userInfo, isRecently]);

    useEffect(() => {
        setCurrentCursor(null);
        setHasMore(true);
        setMaterialList([]);
    }, [isRecently, isTagFilter, id]);
    useEffect(() => {
        !materialList.length && hasMore && fetchMaterialList();
    }, [materialList]);

    useEffect(() => {
        if (isRecently && recentWatermarkUsedList?.materials) {
            setMaterialList(recentWatermarkUsedList?.materials as WatermarkListItem[]);
        }
    }, [recentWatermarkUsedList, isRecently]);
    // 素材曝光埋点
    useListTrack('waterMarkCollapseList', materialList, (data: any) => {
        let { id, category, vip, type } = data;
        if (isNaN(parseInt(category))) {
            category = '';
        }
        window._stat_track?.('tpbj_material_show', {
            func: 'sy',
            material_id: id,
            label_id: category,
            type_id: type,
            belong: 0,
            manage_type: isRecently ? 1 : 0,
            is_search: 1,
            material_vip: vip,
            local_ind: 0,
        });
    });

    const handleCardClick = (data: WatermarkListItem) => {
        const fn = async () => {
            // 防止修改到展示的缩略图
            // await addWatermarkLayer(
            //     WATERMARK_TYPE.TEXT,
            //     { width: editorWidth, height: editorHeight },
            //     '',
            //     {
            //         materialId: String(data.id),
            //         zipUrl: data.zip_file.url
            //     },
            //     data.picture_display_mode === 2,
            //     true
            // );
            setAddData({
                materialId: String(data.id),
                zipUrl: data.zip_file.url,
                mode: data.picture_display_mode,
            });
            // addImgToLayers(image);
            if (userInfo) {
                props.fetchSaveRecentUsed({
                    material_id: +data.id,
                    code: 'watermark',
                    module: 1,
                });
                const usedList: MaterialTopicItem = recentWatermarkUsedList || {
                    id: 'recentUsed',
                    materials: [],
                    name: '常用素材',
                    cursor: '',
                };
                let materials: WatermarkListItem[] = (usedList.materials as WatermarkListItem[]) || [];
                // 过滤数据
                materials = materials.filter((v) => +v.id !== +data.id);

                // 添加数据
                materials.unshift(data);
                usedList.materials = materials;
                setRecentWatermarkUsedList({ ...usedList });
            }
            window._stat_track?.('tpbj_subfunction_click', {
                func: funcID,
                open: 1,
                record_id: recordId,
                click_type: 1,
            });
            window._stat_track?.('tpbj_subfunction_tick', {
                func: funcID,
                record_id: recordId,
            });
            window._stat_track?.('tpbj_material_click', {
                func: funName,
                material_id: data.id,
                label_id: id,
                type_id: data.type_id,
                belong: 0,
                local_ind: 0,
                is_search: 1,
                material_vip: data.threshold,
                manage_type: isRecently ? 1 : 0,
            });
            window._stat_track?.('tpbj_material_tick', {
                func: funName,
                material_id: data.id,
                label_id: id,
                type_id: data.type_id,
                belong: 0,
                local_ind: 0,
                material_vip: data.threshold,
                manage_type: isRecently ? 1 : 0,
            });
        };

        const watermarkLayer = editor.getInstance<WatermarkLayer>('WatermarkLayer').getWatermarkLayer();
        const local = localStorage.getItem(LOCAL_WATERMARK_CONFIRM);
        if (watermarkLayer && !local) {
            watermarkConfirm(
                () => {
                    fn();
                },
                () => {},
            );
            return;
        } else {
            fn();
        }
        // addImgToLayers(image);
    };

    return (
        <div className="c-material-collapse-detail" style={style}>
            <AddWatermark data={addData} setData={setAddData} editor={editor} />
            {!isRecently && !isTagFilter ? (
                <div className="c-material-collapse-detail__title">
                    <BackTitle
                        text={name || $t((v) => v.collapse.watermarkCollapse.materialDetail)}
                        handleBackClick={() => setShowDetailsCollapse(false)}
                    />
                </div>
            ) : null}
            <List<WatermarkListItem>
                bodyClassName="m-material-list"
                loading={loading}
                hasMore={hasMore}
                dataSource={materialList}
                nextPageRequest={fetchMaterialList}
                // emptyNode={() => {
                //     return (
                //         <>
                //             <IconFont name="subtitle" fontSize={64} />
                //             <p>暂无内容</p>
                //             {!userInfo ? (
                //                 <p>请登录,登录后将同步最近使用数据</p>
                //             ) : null}
                //         </>
                //     );
                // }}
                id="waterMarkCollapseList"
                renderItem={(data) => {
                    return (
                        <div key={data.id} draggable="true">
                            <MaterialCard
                                key={data.id}
                                width={120}
                                cardStyle={{
                                    height: 120,
                                    backgroundColor: 'var(--background-system-space-holder)',
                                }}
                                src={imageConverter(formatImageUrl((data as WatermarkListItem).thumbnail.url), 320)}
                                cornerSrc={showSvipCorner ? (data as WatermarkListItem).corner_mark?.url : ''}
                                cornerPlacement="topRight"
                                onClick={() => handleCardClick(data)}
                                trackInfo={{
                                    'data-track-category': id,
                                    'data-track-id': data.id,
                                    'data-track-vip': data.threshold,
                                    'data-track-type': data.type_id,
                                }}
                            />
                        </div>
                    );
                }}
            />
        </div>
    );
};
export default React.memo(WatermarkDetailList);
